<script setup>
import { ref, computed } from "vue";
import {
  DIRECTION_PICKUP,
  DIRECTION_DELIVERY,
  CARRIER_BOOKED_PICKUP,
  CARRIER_BOOKED_ORDER,
  LOADED_AT_SENDER,
  CARRIER_BOOKED_ARRIVAL,
  CARRIER_LOADED_ON_RAIL,
  CARRIER_DEPARTED_BY_RAIL,
  CARRIER_ARRIVED_BY_RAIL,
  PICKED_UP_AT_TERMINAL,
  CANCELLATION_REQUESTED,
  ARRIVED_BY_ROAD,
  DELIVERED_TO_CONSIGNEE,
  TOUR,
  DRIVER,
} from "@/utils/constants";
import { i18n } from "@/i18n/i18n";
import store from "@/store";
import router from "@/router";
import TourCancelModal from "@/components/tours/TourCancelModal.vue";
import OrderDiscardModal from "@/components/orders/OrderDiscardModal.vue";

const { t } = i18n.global;

const props = defineProps(["order", "index"]);
const emit = defineEmits(["close"]);
const showCounterCancellationNeeded = ref(false);
const showOrderDiscardModal = ref(false);
const triggerDiscard = () => {
  if (
    props.order.roadTransportAssignmentStatus === DRIVER ||
    (props.order.roadTransportAssignmentStatus === TOUR &&
      ![
        CARRIER_BOOKED_ORDER,
        CARRIER_DEPARTED_BY_RAIL,
        CARRIER_ARRIVED_BY_RAIL,
        CARRIER_BOOKED_ARRIVAL,
        CARRIER_LOADED_ON_RAIL,
      ].includes(props.order?.transportationStatus))
  ) {
    showOrderDiscardModal.value = true;
  } else if (
    [
      CARRIER_BOOKED_PICKUP,
      PICKED_UP_AT_TERMINAL,
      DELIVERED_TO_CONSIGNEE,
    ].includes(props.order.transportationStatus)
  ) {
    showCounterCancellationNeeded.value = true;
  } else {
    discard();
  }
};

const discard = async () => {
  if (props.order.roadTransportAssignmentStatus === TOUR) {
    await store.dispatch("toggleLoading", true);
    return removeFromTour().then(async (res) => {
      emit("close");
      await store.dispatch("toggleLoading", false);
    });
  }
  await store.dispatch("toggleLoading", false);
  console.err("task did not meet criteria to discard");
};

// const removeOrder = () => { // ENDPOINT FOR TESTING ONLY
//   store.dispatch("OrderStore/removeOrder", {
//     order: props.order,
//     index: props.index,
//   });
// };

const removeFromTour = async () => {
  return await store.dispatch("OrderStore/removeTour", {
    order: props.order,
    index: props.index,
  });
};

const requestOrderCancellation = async () => {
  await store.dispatch("toggleLoading", true);
  let newTransportationStatus = "";
  let endPage = "/";
  let action = "requestCancelOrder";
  if (props.order?.roadTransportAssignmentStatus === DRIVER) {
    newTransportationStatus = CANCELLATION_REQUESTED;
  } else {
    // TOUR or DONE
    if (props.order?.directionType?.toLowerCase() == DIRECTION_PICKUP) {
      switch (props.order.transportationStatus) {
        case CARRIER_BOOKED_PICKUP:
        case PICKED_UP_AT_TERMINAL:
        case DELIVERED_TO_CONSIGNEE:
        case ARRIVED_BY_ROAD:
        case CARRIER_DEPARTED_BY_RAIL:
          newTransportationStatus = CANCELLATION_REQUESTED;
          endPage = "/tour";
          action = "updateTourOrder";
          break;
        default:
          break;
      }
    } else {
      switch (props.order.transportationStatus) {
        case LOADED_AT_SENDER:
        case ARRIVED_BY_ROAD:
        case CARRIER_BOOKED_ARRIVAL:
        case CARRIER_LOADED_ON_RAIL:
        case CARRIER_DEPARTED_BY_RAIL:
          newTransportationStatus = CANCELLATION_REQUESTED;
          endPage = "/tour";
          action = "updateTourOrder";
          break;
        default:
          break;
      }
    }
  }

  if (newTransportationStatus) {
    await store
      .dispatch(`OrderStore/${action}`, {
        order: props.order,
        index: props.index,
        params: {
          driverId: store.state.user.data.user_id,
          transportationStatus: newTransportationStatus,
        },
      })
      .catch(async (error) => {
        await store.dispatch("toggleLoading", false);
      });
  } else {
    console.error(
      "We don't know how to handle this transportationStatus yet. Please update code."
    );
    await store.dispatch("toggleLoading", false);
  }

  showCounterCancellationNeeded.value = false;
  emit("close");
  emit("refreshData");
  await store.dispatch("toggleLoading", false);

  router.push(endPage);
};

const setButtonString = computed(() => {
  if (props.order?.directionType?.toLowerCase() == DIRECTION_DELIVERY) {
    if (
      props.order.roadTransportAssignmentStatus === DRIVER &&
      props.order?.transportationStatus == CARRIER_BOOKED_ORDER
    ) {
      return t("order.discard_task");
    } else if (props.order?.transportationStatus == CARRIER_BOOKED_ORDER) {
      return t("tour.remove_from_tour");
    } else if (props.order?.transportationStatus == LOADED_AT_SENDER) {
      return t("order.discard_task");
    } else if (props.order?.transportationStatus == ARRIVED_BY_ROAD) {
      return t("order.discard_task");
    } else if (props.order?.transportationStatus == CARRIER_BOOKED_ARRIVAL) {
      return t("order.discard_task");
    } else if (
      [CARRIER_LOADED_ON_RAIL, CARRIER_DEPARTED_BY_RAIL].includes(
        props.order?.transportationStatus
      )
    ) {
      return t("order.discard_task");
    }
  } else if (props.order?.directionType?.toLowerCase() == DIRECTION_PICKUP) {
    if (
      props.order.roadTransportAssignmentStatus === DRIVER &&
      [CARRIER_DEPARTED_BY_RAIL, CARRIER_ARRIVED_BY_RAIL].includes(
        props.order?.transportationStatus
      )
    ) {
      return t("order.discard_task");
    } else if (
      props.order.roadTransportAssignmentStatus === TOUR &&
      [
        CARRIER_DEPARTED_BY_RAIL,
        CARRIER_ARRIVED_BY_RAIL,
        LOADED_AT_SENDER,
        CARRIER_BOOKED_ARRIVAL,
        CARRIER_LOADED_ON_RAIL,
      ].includes(props.order?.transportationStatus)
    ) {
      return t("tour.remove_from_tour");
    } else if (props.order?.transportationStatus === CARRIER_BOOKED_PICKUP) {
      return t("order.discard_task");
    } else if (props.order?.transportationStatus === PICKED_UP_AT_TERMINAL) {
      return t("order.discard_task");
    }
  }

  if (props.order.roadTransportAssignmentStatus === DRIVER) {
    return t("order.discard_task");
  }
  return t("tour.remove_from_tour");
});
</script>
<template>
  <db-button
    @click="triggerDiscard"
    class="w-full hydrated"
    data-testid="test-discard"
    >{{ setButtonString }}</db-button
  >

  <TourCancelModal
    :visible="showCounterCancellationNeeded"
    @hideModal="showCounterCancellationNeeded = false"
    @discard="requestOrderCancellation"
  />
  <OrderDiscardModal
    :visible="showOrderDiscardModal"
    @hideModal="showOrderDiscardModal = false"
    @discard="requestOrderCancellation"
  />
</template>
